/**
 * 路径匹配器
 * @param {string} pattern
 * @param {string} path
 * @returns {Boolean}
 */
export function isPathMatch(pattern, path) {
  const regexPattern = pattern.replace(/\//g, '\\/').replace(/\*\*/g, '.*').replace(/\*/g, '[^\\/]*')
  const regex = new RegExp(`^${regexPattern}$`)
  return regex.test(path)
}

/**
 * 生成 Element UI 表单校验器：字符串去首尾空格后不能为空
 * （async-validator 的 required 不拦截纯空格）
 * @param {string} label 字段名称，用于拼接提示语
 * @returns {Function}
 */
export function notBlankValidator(label) {
  return (rule, value, callback) => {
    if (typeof value === 'string' && value.trim() === '') {
      callback(new Error(`${label}不能为空`))
    } else {
      callback()
    }
  }
}

/**
 * 去除字符串首尾空格，非字符串原样返回
 * @param {*} value
 * @returns {*}
 */
export function trimString(value) {
  return typeof value === 'string' ? value.trim() : value
}

/** 英文名称：只允许英文字母、数字、空格及 - _ & . , ( ) /，且至少包含一个字母 */
export const EN_NAME_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9 \-_&.,()/]+$/

/** 代码：只允许字母、数字、- 和 _，且至少包含一个字母或数字 */
export const CODE_PATTERN = /^(?=.*[A-Za-z0-9])[A-Za-z0-9_-]+$/

/**
 * 输入过滤：只保留字母、数字、- 和 _（与 CODE_PATTERN 的允许集一致），
 * 用于仓库代码/分区代码/库位类型代码/库位代码等代码输入框，敲入即剔除
 * @、!、空格、中文等特殊字符。
 * 说明：中文输入法组合态的拼音也会被剔除，属预期——代码字段本就只允许 ASCII。
 * 典型用法：<el-input v-model="x" @input="x = codeCharsOnly($event)"/>
 */
export function codeCharsOnly(value) {
  return value === null || value === undefined ? '' : String(value).replace(/[^A-Za-z0-9_-]/g, '');
}

/** 名称内容：至少包含一个中文、字母或数字（拒绝纯符号） */
export const TEXT_PATTERN = /[A-Za-z0-9\u4e00-\u9fa5]/

/** 邮编：只允许数字 */
export const POSTAL_CODE_PATTERN = /^\d+$/

/**
 * 生成正则校验器：非必填，填写时按正则校验（空值由 required / notBlankValidator 负责）
 * @param {string} label 字段名称，用于拼接提示语
 * @param {RegExp} pattern 校验正则
 * @param {string} message 不通过时的提示后缀
 * @returns {Function}
 */
export function patternValidator(label, pattern, message) {
  return (rule, value, callback) => {
    const trimmed = trimString(value)
    if (trimmed === '' || trimmed === null || trimmed === undefined) {
      callback()
    } else if (!pattern.test(trimmed)) {
      callback(new Error(`${label}${message}`))
    } else {
      callback()
    }
  }
}

/**
 * 生成 Element UI 表单校验器：数值不能为负数（空值由 required 负责）
 * @param {string} label 字段名称，用于拼接提示语
 * @returns {Function}
 */
export function nonNegativeValidator(label) {
  return (rule, value, callback) => {
    if (value === null || value === undefined || value === '') {
      callback()
    } else if (typeof value !== 'number' || Number.isNaN(value)) {
      callback(new Error(`${label}必须为数字`))
    } else if (value < 0) {
      callback(new Error(`${label}不能为负数`))
    } else {
      callback()
    }
  }
}

/**
 * 生成 Element UI 表单校验器：日期对象、下拉框的数字 id 等非文本字段的必填校验
 *
 * 为什么不能直接用 { required: true }（本项目的 async-validator 是 1.8.5）：
 * 规则只要带上 trigger 等其它键，就会退化成 string 类型的校验器，
 * Date 对象和数字会被误判成“未填”，字段填了也一直报必填；
 * 改成 type: 'date' 又会在编辑回显时炸——后端对没有 @JsonFormat 的 LocalDateTime
 * 返回的是 ISO 字符串，date 校验器拿到字符串会调 getTime() 抛 TypeError。
 * 这里按“值为 null / undefined / 空串 / 空数组 即未填”判断，两种取值形态都安全。
 * @param {string} message 未填时的提示语
 * @returns {Function}
 */
export function requiredValueValidator(message) {
  return (rule, value, callback) => {
    const empty = value === null
        || value === undefined
        || (typeof value === 'string' && value.trim() === '')
        || (Array.isArray(value) && value.length === 0)
    if (empty) {
      callback(new Error(message))
    } else {
      callback()
    }
  }
}

/** 超长超重计费公式支持的变量（与后端 checkFormula 一致，区分大小写，多字母变量先替换） */
const FORMULA_VARS = /\b(?:CW|GW|VT|L|M|S)\b/g

/** 变量存在性判断用非全局正则：全局正则的 test 会推进 lastIndex，复用会误判 */
const FORMULA_HAS_VAR = /\b(?:CW|GW|VT|L|M|S)\b/

/** 公式允许的字符：数字、小数点、空格、四则运算、括号、比较符（and / or 已在上一步剔除） */
const FORMULA_ALLOWED_CHARS = /^[0-9.\s+\-*/()<>=!]+$/

/** 比较运算符：>= <= == != 需先于 > < 匹配，避免被拆成两个单字符 */
const FORMULA_COMPARATORS = /(?:>=|<=|==|!=|>|<)/g

/**
 * 校验超长超重计费公式，口径与后端 OverLengthWeightRuleServiceImpl#checkFormula 对齐。
 * 后端对空公式、非法公式、执行报错的公式一律按「未命中」处理且不抛异常，
 * 也就是公式写错时不报错、只是静默不收附加费，所以必须在保存前拦住。
 * @param {string} value 公式文本
 * @returns {string} 不合法时的提示语，合法时返回空字符串
 */
export function checkOverLengthWeightFormula(value) {
  const formula = typeof value === 'string' ? value.trim() : ''
  if (!formula) {
    return '公式不能为空'
  }
  // 变量与 and / or 之外的内容一律不允许：后端只替换小写 \band\b、\bor\b，
  // 大写 AND / OR 与 && / || 不会被替换，执行时直接报脚本错误
  const residue = formula.replace(FORMULA_VARS, ' ').replace(/\b(?:and|or)\b/g, ' ')
  if (/[A-Za-z]/.test(residue)) {
    if (/\b(?:AND|OR)\b/i.test(residue)) {
      return '逻辑运算符请使用小写 and、or'
    }
    return '公式含不支持的变量或字符，仅支持 L、M、S、CW、GW、VT 及 and、or'
  }
  if (!FORMULA_ALLOWED_CHARS.test(residue)) {
    return '公式含不支持的符号，仅支持 + - * / ( ) > < >= <= == != 及 and、or'
  }
  if (!FORMULA_HAS_VAR.test(formula)) {
    return '公式至少需要包含一个变量（L、M、S、CW、GW、VT）'
  }
  // 把双字符比较符占位后，残留的 = / ! 是单写的赋值号或感叹号：
  // 后端会原样交给 JS 引擎，L=300 变成赋值、结果恒为真，会错收费
  const restAfterComparators = formula.replace(FORMULA_COMPARATORS, '#')
  if (restAfterComparators.includes('=')) {
    return '单独的 = 是赋值不是比较，等于请写 =='
  }
  if (restAfterComparators.includes('!')) {
    return '单独的 ! 不支持，不等于请写 !='
  }
  // 比较符不能连写：>> 、<< 是 JS 的移位运算符，能被引擎算成数字，后端把数字按 >0 判命中
  if (/>>|<</.test(formula)) {
    return '比较运算符不能连写，如 >> 、<<'
  }
  // 括号与 and / or 只是分组和连接，剥掉后每一段都必须是一个完整比较式。
  // "L!=300 and M" 这类只写变量的写法能被 JS 引擎算成数字，后端按数字 >0 判命中，
  // 会在 M 不为 0 时错收费，且 and / or 悬空同样只会在运行期静默失效
  const conditions = formula
      .replace(/\b(?:and|or)\b/g, '|')
      .replace(/[()]/g, ' ')
      .split('|')
      .map(item => item.trim())
  if (conditions.some(item => !item || !/(?:>=|<=|==|!=|>|<)/.test(item))) {
    return '每个条件都要写比较符（> < >= <= == !=），如 L>300、CW>=20'
  }
  let depth = 0
  for (const char of formula) {
    if (char === '(') depth++
    else if (char === ')') depth--
    if (depth < 0) {
      return '公式括号不匹配'
    }
  }
  if (depth !== 0) {
    return '公式括号不匹配'
  }
  // 变量代入数值后按 JS 语法编译一遍（只编译不执行），拦住 L>300 and、>300 这类残缺式；
  // 后端 checkFormula 用的就是 JS 引擎，这里的判定与它一致。CSP 禁用 eval 时抛的是
  // EvalError 而非 SyntaxError，此时跳过该检查，不能误判用户写错
  const compiled = formula
      .replace(FORMULA_VARS, '0')
      .replace(/\b(?:and|or)\b/g, m => (m === 'and' ? '&&' : '||'))
  try {
    new Function(`return (${compiled})`)
  } catch (e) {
    if (e instanceof SyntaxError) {
      return '公式语法有误，请检查每个运算符两侧是否都有内容'
    }
  }
  return ''
}

/** 单个国家编码：ISO 3166-1 alpha-2 两位大写字母，如 BR */
export const COUNTRY_CODE_ITEM_PATTERN = /^[A-Z]{2}$/

/**
 * 校验超长超重计费规则选中的国家（多选下拉，值为 alpha-2 编码数组）。
 * 后端 FIND_IN_SET(订单收件国, country_codes) 匹配，且已强校验每个编码必须
 * 存在于国家字典的启用项。这里提前拦两类问题：
 *   1) 一行国家都没选 —— 规则永不命中，但保存不报错，属于静默失效；
 *   2) 编辑老规则时带进来的、字典里已查不到的编码（如历史的小写或三位码）。
 * @param {string[]} value 选中的国家编码数组
 * @param {string[]} allowedCodes 国家字典中启用的编码列表；为空（字典没加载出来）时不做成员校验，
 *        免得把下拉取数的偶发失败变成"保存不了"
 * @returns {string} 不合法时的提示语，合法时返回空字符串
 */
export function checkCountryCodes(value, allowedCodes) {
  const codes = Array.isArray(value)
    ? value.map(c => (c === null || c === undefined ? '' : String(c).trim())).filter(Boolean)
    : []
  if (!codes.length) {
    return '请至少选择一个国家，不选国家该规则不会命中任何运单'
  }
  const allowList = Array.isArray(allowedCodes) ? allowedCodes : []
  const unknown = codes.filter(code =>
    !COUNTRY_CODE_ITEM_PATTERN.test(code.toUpperCase())
    || (allowList.length > 0 && allowList.indexOf(code.toUpperCase()) === -1)
  )
  if (unknown.length) {
    return `国家编码不在国家字典中：${unknown.join('、')}，请重新选择（字典口径为 ISO 两位大写字母，如 BR）`
  }
  return ''
}

/**
 * 判断value字符串是否为空
 * @param {string} value
 * @returns {Boolean}
 */
export function isEmpty(value) {
  if (value == null || value == "" || value == undefined || value == "undefined") {
    return true
  }
  return false
}

/**
 * 判断url是否是http或https 
 * @param {string} url
 * @returns {Boolean}
 */
export function isHttp(url) {
  return url.indexOf('http://') !== -1 || url.indexOf('https://') !== -1
}

/**
 * 判断path是否为外链
 * @param {string} path
 * @returns {Boolean}
 */
export function isExternal(path) {
  return /^(https?:|mailto:|tel:)/.test(path)
}

/**
 * @param {string} str
 * @returns {Boolean}
 */
export function validUsername(str) {
  const valid_map = ['admin', 'editor']
  return valid_map.indexOf(str.trim()) >= 0
}

/**
 * @param {string} url
 * @returns {Boolean}
 */
export function validURL(url) {
  const reg = /^(https?|ftp):\/\/([a-zA-Z0-9.-]+(:[a-zA-Z0-9.&%$-]+)*@)*((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3}|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.(com|edu|gov|int|mil|net|org|biz|arpa|info|name|pro|aero|coop|museum|[a-zA-Z]{2}))(:[0-9]+)*(\/($|[a-zA-Z0-9.,?'\\+&%$#=~_-]+))*$/
  return reg.test(url)
}

/**
 * @param {string} str
 * @returns {Boolean}
 */
export function validLowerCase(str) {
  const reg = /^[a-z]+$/
  return reg.test(str)
}

/**
 * @param {string} str
 * @returns {Boolean}
 */
export function validUpperCase(str) {
  const reg = /^[A-Z]+$/
  return reg.test(str)
}

/**
 * @param {string} str
 * @returns {Boolean}
 */
export function validAlphabets(str) {
  const reg = /^[A-Za-z]+$/
  return reg.test(str)
}

/**
 * @param {string} email
 * @returns {Boolean}
 */
export function validEmail(email) {
  const reg = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return reg.test(email)
}

/**
 * @param {string} str
 * @returns {Boolean}
 */
export function isString(str) {
  return typeof str === 'string' || str instanceof String
}

/**
 * @param {Array} arg
 * @returns {Boolean}
 */
export function isArray(arg) {
  if (typeof Array.isArray === 'undefined') {
    return Object.prototype.toString.call(arg) === '[object Array]'
  }
  return Array.isArray(arg)
}
